<template>
    <header class="ps-tool-nav" :class="{ 'ps-tool-nav--open': mobileOpen }">
        <div class="ps-tool-nav__bar">
            <div class="ps-tool-nav__inner">
                <!-- Brand + breadcrumb -->
                <div class="ps-tool-nav__start">
                    <Link :href="route('home')" class="ps-tool-nav__logo" aria-label="ProfileSuite home">
                        <logo class="ps-tool-nav__logo-img" />
                    </Link>

                    <span class="ps-tool-nav__mobile-current sm:hidden">
                        <span class="ps-tool-nav__current-icon">
                            <icon :name="currentTool.icon" class="w-3.5 h-3.5" />
                        </span>
                        <span class="ps-tool-nav__current-label">{{ currentTool.name }}</span>
                    </span>

                    <nav class="ps-tool-nav__crumb" aria-label="Breadcrumb">
                        <Link :href="route('tools.show', 'print-studio')" class="ps-tool-nav__crumb-link">
                            {{ __('Tools') }}
                        </Link>
                        <svg class="ps-tool-nav__crumb-sep" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L10.94 10 7.23 6.29a.75.75 0 111.06-1.06l4.25 4.25a.75.75 0 010 1.06l-4.25 4.25a.75.75 0 01-1.06 0z" clip-rule="evenodd" />
                        </svg>
                        <span class="ps-tool-nav__current">
                            <span class="ps-tool-nav__current-icon">
                                <icon :name="currentTool.icon" class="w-3.5 h-3.5" />
                            </span>
                            <span class="ps-tool-nav__current-label">{{ currentTool.name }}</span>
                        </span>
                    </nav>
                </div>

                <!-- Desktop tool switcher -->
                <div
                    class="ps-tool-nav__switcher hidden lg:block"
                    @mouseenter="openSwitcher"
                    @mouseleave="closeSwitcher"
                >
                    <button
                        type="button"
                        class="ps-tool-nav__switcher-btn"
                        :class="{ 'ps-tool-nav__switcher-btn--open': switcherOpen }"
                        aria-haspopup="true"
                        :aria-expanded="switcherOpen ? 'true' : 'false'"
                        @click="switcherOpen = !switcherOpen"
                    >
                        <icon name="category" class="w-4 h-4 shrink-0" />
                        {{ __('Switch tool') }}
                        <svg class="ps-tool-nav__chevron" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clip-rule="evenodd" />
                        </svg>
                    </button>

                    <div v-show="switcherOpen" class="ps-tool-nav__mega">
                        <div class="ps-tool-nav__mega-grid">
                            <div v-for="group in toolGroups" :key="group.id" class="ps-tool-nav__mega-col">
                                <p class="ps-tool-nav__mega-title">{{ __(group.title) }}</p>
                                <ul class="ps-tool-nav__mega-list">
                                    <li v-for="tool in group.tools" :key="tool.slug">
                                        <Link
                                            :href="route('tools.show', tool.slug)"
                                            class="ps-tool-nav__mega-item"
                                            :class="{ 'ps-tool-nav__mega-item--active': tool.slug === currentSlug }"
                                            @click="closePanels"
                                        >
                                            <span class="ps-tool-nav__mega-icon">
                                                <icon :name="tool.icon" class="w-4 h-4" />
                                            </span>
                                            <span>
                                                <span class="ps-tool-nav__mega-name">{{ tool.name }}</span>
                                                <span class="ps-tool-nav__mega-desc">{{ tool.desc }}</span>
                                            </span>
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Actions -->
                <div class="ps-tool-nav__actions">
                    <Link
                        :href="route('home')"
                        class="ps-tool-nav__ghost hidden sm:inline-flex"
                        :title="__('Marketing site')"
                    >
                        <icon name="home" class="w-4 h-4" />
                        <span class="hidden xl:inline">{{ __('Home') }}</span>
                    </Link>

                    <Link
                        v-if="$page.props.auth && $page.props.auth.user"
                        :href="route('dashboard')"
                        class="ps-tool-nav__admin hidden sm:inline-flex"
                    >
                        <icon name="dashboard" class="w-4 h-4" />
                        <span class="hidden xl:inline">{{ __('Admin') }}</span>
                    </Link>
                    <Link
                        v-else
                        :href="route('login')"
                        class="ps-tool-nav__cta hidden sm:inline-flex"
                    >
                        {{ __('Login') }}
                    </Link>

                    <button
                        type="button"
                        class="ps-tool-nav__toggle lg:hidden"
                        :aria-expanded="mobileOpen ? 'true' : 'false'"
                        aria-controls="ps-tool-nav-mobile"
                        aria-label="Toggle tools menu"
                        @click="toggleMobile"
                    >
                        <span class="ps-tool-nav__toggle-bar" />
                        <span class="ps-tool-nav__toggle-bar" />
                        <span class="ps-tool-nav__toggle-bar" />
                    </button>
                </div>
            </div>
        </div>

        <!-- Mobile overlay -->
        <div
            v-show="mobileOpen"
            class="ps-tool-nav__overlay lg:hidden"
            aria-hidden="true"
            @click="closePanels"
        />

        <!-- Mobile drawer -->
        <div
            id="ps-tool-nav-mobile"
            class="ps-tool-nav__mobile lg:hidden"
            :class="{ 'ps-tool-nav__mobile--open': mobileOpen }"
        >
            <div class="ps-tool-nav__mobile-head">
                <p class="ps-tool-nav__mobile-title">{{ __('Switch tool') }}</p>
                <button type="button" class="ps-tool-nav__mobile-close" aria-label="Close menu" @click="closePanels">
                    <icon name="close" class="w-5 h-5" />
                </button>
            </div>

            <div class="ps-tool-nav__mobile-body">
                <div v-for="group in toolGroups" :key="'m-' + group.id" class="ps-tool-nav__mobile-group">
                    <p class="ps-tool-nav__mobile-section">{{ __(group.title) }}</p>
                    <ul class="ps-tool-nav__mobile-list">
                        <li v-for="tool in group.tools" :key="'mt-' + tool.slug">
                            <Link
                                :href="route('tools.show', tool.slug)"
                                class="ps-tool-nav__mobile-item"
                                :class="{ 'ps-tool-nav__mobile-item--active': tool.slug === currentSlug }"
                                @click="closePanels"
                            >
                                <span class="ps-tool-nav__mobile-icon">
                                    <icon :name="tool.icon" class="w-4 h-4" />
                                </span>
                                <span>
                                    <span class="ps-tool-nav__mobile-name">{{ tool.name }}</span>
                                    <span class="ps-tool-nav__mobile-desc">{{ tool.desc }}</span>
                                </span>
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>

            <div class="ps-tool-nav__mobile-foot">
                <Link :href="route('home')" class="ps-tool-nav__mobile-foot-link" @click="closePanels">
                    <icon name="home" class="w-4 h-4" />
                    {{ __('Marketing site') }}
                </Link>
                <Link
                    v-if="$page.props.auth && $page.props.auth.user"
                    :href="route('dashboard')"
                    class="ps-tool-nav__mobile-cta"
                    @click="closePanels"
                >
                    {{ __('Admin dashboard') }}
                </Link>
                <Link
                    v-else
                    :href="route('login')"
                    class="ps-tool-nav__mobile-cta"
                    @click="closePanels"
                >
                    {{ __('Login') }}
                </Link>
            </div>
        </div>
    </header>
</template>

<script>
import { Link } from '@inertiajs/vue3'
import Logo from '@/Shared/Logo'
import Icon from '@/Shared/Icon'
import { TOOL_GROUPS, findToolBySlug, slugFromPageUrl } from '@/utils/toolsRegistry'

export default {
    components: {
        Link,
        Logo,
        Icon,
    },
    data() {
        return {
            switcherOpen: false,
            switcherCloseTimer: null,
            mobileOpen: false,
            toolGroups: TOOL_GROUPS,
        }
    },
    computed: {
        currentSlug() {
            return slugFromPageUrl(this.$page.url)
        },
        currentTool() {
            return findToolBySlug(this.currentSlug)
        },
    },
    watch: {
        '$page.url'() {
            this.closePanels()
        },
    },
    methods: {
        openSwitcher() {
            if (this.switcherCloseTimer) {
                clearTimeout(this.switcherCloseTimer)
                this.switcherCloseTimer = null
            }
            this.switcherOpen = true
        },
        closeSwitcher() {
            this.switcherCloseTimer = setTimeout(() => {
                this.switcherOpen = false
            }, 120)
        },
        toggleMobile() {
            this.mobileOpen = !this.mobileOpen
            document.body.style.overflow = this.mobileOpen ? 'hidden' : ''
        },
        closePanels() {
            this.mobileOpen = false
            this.switcherOpen = false
            document.body.style.overflow = ''
            if (this.switcherCloseTimer) {
                clearTimeout(this.switcherCloseTimer)
                this.switcherCloseTimer = null
            }
        },
        onKeydown(event) {
            if (event.key === 'Escape') {
                this.closePanels()
            }
        },
    },
    mounted() {
        window.addEventListener('keydown', this.onKeydown)
    },
    beforeUnmount() {
        window.removeEventListener('keydown', this.onKeydown)
        document.body.style.overflow = ''
        if (this.switcherCloseTimer) {
            clearTimeout(this.switcherCloseTimer)
        }
    },
}
</script>
